import {
  OfflineState,
  useOfflineContext,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/offline-context";
import { toast } from "sonner";
import {
  deleteDocumentDB,
  initDocumentDB,
} from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { RefreshCw } from "lucide-react";
import { useGetWorkOrder, useWorkOrderOffline } from "./use-work-order-offline";
import { useWorkOrderPositionsOffline } from "./use-work-order-positions-offline";
import { useAirHandlingUnitDataOffline } from "./use-air-handling-unit-data-offline";
import { useAirHandlingUnitOffline } from "./use-air-handling-unit-offline";
import { useAhuComponentCatalogOffline } from "./use-ahu-component-catalog-offline";
import { usePositionCatalogOffline } from "./use-position-catalog-offline";
import { useStructuralAssessmentCatalogOffline } from "./use-structural-assessment-catalog-offline";
import { useVisualAssessmentCatalogOffline } from "./use-visual-assessment-catalog-offline";
import { useDevicesOffline } from "./use-devices-offline";
import { useLaboratoriesOffline } from "./use-laboratories-offline";
import { useOrdersOffline } from "./use-orders-offline";
import { useOffersOffline } from "./use-offers-offline";
import { useAirMicrobialSampleSetOffline } from "./use-air-microbial-sample-set-offline";
import { useDustDensitySampleOffline } from "./use-dust-density-sample-offline";
import { useSurfaceMicrobialSampleSetOffline } from "./use-surface-microbial-sample-set-offline";
import { useWaterSampleOffline } from "./use-water-sample-offline";
import { useBooleanFlagValue } from "@openfeature/react-sdk";

const objectStoreNames = [
  { name: "ahuComponentCatalog", keyPath: "id" },
  { name: "airHandlingUnit", keyPath: "id" },
  { name: "airHandlingUnitData", keyPath: "id" },
  { name: "airMicrobialSampleSet", keyPath: "id" },
  { name: "devices", keyPath: "id" },
  { name: "dustDensitySample", keyPath: "id" },
  { name: "laboratories", keyPath: "id" },
  { name: "orders", keyPath: "id" },
  { name: "offers", keyPath: "id" },
  { name: "position", keyPath: "id" },
  { name: "positionCatalog", keyPath: "id" },
  { name: "structuralAssessmentCatalog", keyPath: "id" },
  { name: "surfaceMicrobialSampleSet", keyPath: "id" },
  { name: "visualAssessmentCatalog", keyPath: "id" },
  { name: "waterSample", keyPath: "id" },
  { name: "workOrder", keyPath: "id" },
];

export function OfflineButton({ workOrderId }: { workOrderId: string }) {
  const isReleased = useBooleanFlagValue("gsm.htz.workorders.offline", false);
  const { offlineState, setOfflineState, setSynced } = useOfflineContext();

  const { data: workOrder } = useGetWorkOrder(workOrderId);
  const processId = workOrder?.processId;
  const companyId = workOrder?.companyId;
  const customerId = workOrder?.customerId;

  const { prepareWorkOrder, syncWorkOrder } = useWorkOrderOffline(workOrderId);
  const { preparePositions, syncPositions } =
    useWorkOrderPositionsOffline(workOrderId);
  const { prepare: prepareAirHandlingUnits } = useAirHandlingUnitOffline(
    workOrderId,
    customerId!,
  );
  const { prepareAirHandlingUnitData, syncAirHandlingUnitData } =
    useAirHandlingUnitDataOffline(workOrderId, customerId!);
  const { prepare: prepareAhuComponentCatalog } = useAhuComponentCatalogOffline(
    workOrderId,
    companyId!,
  );
  const { prepare: preparePositionCatalog } = usePositionCatalogOffline(
    workOrderId,
    companyId!,
  );
  const { prepare: prepareStructuralAssessmentCatalog } =
    useStructuralAssessmentCatalogOffline(workOrderId, companyId!);
  const { prepare: prepareVisualAssessmentCatalog } =
    useVisualAssessmentCatalogOffline(workOrderId, companyId!);
  const { prepare: prepareDevices } = useDevicesOffline(workOrderId);
  const { prepare: prepareLaboratories } = useLaboratoriesOffline(workOrderId);
  const { prepare: prepareOrders } = useOrdersOffline(workOrderId, processId!);
  const { prepare: prepareOffers } = useOffersOffline(workOrderId, processId!);
  const { prepareAirMicrobialSampleSets, syncAirMicrobialSampleSets } =
    useAirMicrobialSampleSetOffline(workOrderId);
  const { prepareDustDensitySamples, syncDustDensitySamples } =
    useDustDensitySampleOffline(workOrderId);
  const { prepareSurfaceMicrobialSampleSets, syncSurfaceMicrobialSampleSets } =
    useSurfaceMicrobialSampleSetOffline(workOrderId);
  const { prepareWaterSamples, syncWaterSamples } =
    useWaterSampleOffline(workOrderId);

  if (!isReleased) {
    return null;
  }

  const goOfflineHandler = async () => {
    try {
      setSynced([t("Initialisiere Offline Datenbank ...")]);
      await setOfflineState(OfflineState.PREPARING_OFFLINE);

      // cleanup potentially stale data for the document
      await deleteDocumentDB(GSMDocumentKind.WorkOrder, workOrderId);

      // create database for document
      const initialized = await initDocumentDB(
        GSMDocumentKind.WorkOrder,
        workOrderId,
        objectStoreNames,
      );
      if (!initialized) {
        toast.error(t("Offline Datenbank konnte nicht initialisiert werden."));
        return;
      }
      setSynced([t("Datenbank initialisiert")]);

      // load all data into IndexedDB
      await Promise.all([
        prepareWorkOrder(),
        preparePositions(),
        prepareAirHandlingUnits(),
        prepareAirHandlingUnitData(),
        prepareAhuComponentCatalog(),
        preparePositionCatalog(),
        prepareStructuralAssessmentCatalog(),
        prepareVisualAssessmentCatalog(),
        prepareDevices(),
        prepareLaboratories(),
        prepareOrders(),
        prepareOffers(),
        prepareAirMicrobialSampleSets(),
        prepareDustDensitySamples(),
        prepareSurfaceMicrobialSampleSets(),
        prepareWaterSamples(),
      ]);

      await setOfflineState(OfflineState.OFFLINE);
    } catch (err) {
      toast.error(
        err !== null && typeof err === "object" && "message" in err
          ? (err.message as string)
          : t("Fehler beim Vorbereiten des Offline Modus"),
      );
      await setOfflineState(OfflineState.ONLINE);
    } finally {
      setSynced([]);
    }
  };

  const synchronizeHandler = async () => {
    try {
      await setOfflineState(OfflineState.SYNCING);
      setSynced([]);

      // The order of synchronization matters.
      // Positions hold foreign key relations to ahu data.
      // Prefer running this sequentially.
      await syncAirHandlingUnitData();
      await syncWorkOrder();
      await syncPositions();
      await syncAirMicrobialSampleSets();
      await syncDustDensitySamples();
      await syncSurfaceMicrobialSampleSets();
      await syncWaterSamples();

      await deleteDocumentDB(GSMDocumentKind.WorkOrder, workOrderId);
      await setOfflineState(OfflineState.ONLINE);
    } catch (err) {
      toast.error(
        err !== null && typeof err === "object" && "message" in err
          ? (err.message as string)
          : t("Fehler beim Synchronisieren"),
      );
      await setOfflineState(OfflineState.OFFLINE);
    } finally {
      setSynced([]);
    }
  };

  switch (offlineState) {
    case OfflineState.ONLINE:
      return (
        <Button
          variant="secondary"
          onClick={goOfflineHandler}
          disabled={workOrder === undefined}
        >
          {t("Offline aktivieren")}
        </Button>
      );
    case OfflineState.PREPARING_OFFLINE:
      return (
        <Button variant="secondary" disabled>
          <RefreshCw className="mr-2 h-4 w-4" />
          <span>{t("Offline Vorbereiten ...")}</span>
        </Button>
      );
    case OfflineState.OFFLINE:
      return (
        <Button
          variant="secondary"
          onClick={synchronizeHandler}
          disabled={offlineState !== OfflineState.OFFLINE}
        >
          <span>{t("Synchronisieren")}</span>
        </Button>
      );
    case OfflineState.SYNCING:
      return (
        <Button variant="secondary" disabled>
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          <span>{t("Synchronisiere ...")}</span>
        </Button>
      );
    default:
      return null;
  }
}
